import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { useServiceProduct } from "@/hooks/site/useProducts";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";

type Form = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  pickup_address: string;
  pickup_city: string;
  pickup_state: string;
  pickup_zip: string;
  dropoff_address: string;
  dropoff_city: string;
  dropoff_state: string;
  dropoff_zip: string;
  move_date: string;
  move_time: string;
  notes: string;
};

const EMPTY: Form = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  pickup_address: "",
  pickup_city: "",
  pickup_state: "",
  pickup_zip: "",
  dropoff_address: "",
  dropoff_city: "",
  dropoff_state: "",
  dropoff_zip: "",
  move_date: "",
  move_time: "09:00",
  notes: "",
};

export default function SiteCheckoutPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { product, loading } = useServiceProduct(id);
  const [form, setForm] = useState<Form>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prefill email from logged-in user
  useEffect(() => {
    if (user?.email && !form.email) {
      setForm((f) => ({ ...f, email: user.email ?? "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const unitPrice = useMemo(() => {
    if (!product) return 0;
    return product.discount_enabled && product.discount_price != null
      ? product.discount_price
      : product.price;
  }, [product]);

  const taxRate = 0.0875; // 8.75% sales tax
  const subtotal = unitPrice;
  const taxTotal = +(subtotal * taxRate).toFixed(2);
  const total = +(subtotal + taxTotal).toFixed(2);

  const update = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };

  const requiredOk =
    form.first_name.trim() &&
    form.last_name.trim() &&
    form.email.trim() &&
    form.phone.trim() &&
    form.pickup_address.trim() &&
    form.pickup_city.trim() &&
    form.dropoff_address.trim() &&
    form.dropoff_city.trim() &&
    form.move_date.trim();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!product || !requiredOk || submitting) return;
    setError(null);
    setSubmitting(true);

    const recipient = {
      name: `${form.first_name.trim()} ${form.last_name.trim()}`.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      address: form.pickup_address.trim(),
      city: form.pickup_city.trim(),
      state: form.pickup_state.trim(),
      zip: form.pickup_zip.trim(),
      dropoff_address: form.dropoff_address.trim(),
      dropoff_city: form.dropoff_city.trim(),
      dropoff_state: form.dropoff_state.trim(),
      dropoff_zip: form.dropoff_zip.trim(),
      move_date: form.move_date,
      move_time: form.move_time,
    };

    try {
      const { data: orderData, error: orderErr } = await supabase
        .from("order_headers")
        .insert({
          currency: "USD",
          payment_provider: "pending",
          status: "pending_payment",
          subtotal_items: subtotal,
          shipping_total: 0,
          tax_total: taxTotal,
          discount_price: 0,
          customer_notes: form.notes.trim() || null,
          customer_id: user?.id ?? null,
          recipient,
        })
        .select("id")
        .single();

      if (orderErr) throw orderErr;

      const { error: itemErr } = await supabase.from("order_items").insert([
        {
          order_id: orderData.id,
          product_id: String(product.id),
          product_name: product.name,
          quantity: 1,
          unit_price: unitPrice,
          final_price: unitPrice,
          subtotal: unitPrice,
        },
      ]);

      if (itemErr) throw itemErr;

      navigate(`/site/order-confirmation/${orderData.id}`);
    } catch (err: any) {
      setError(err.message ?? "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <SiteLayout navVariant="solid">
        <div className="min-h-[60vh] pt-32 flex items-center justify-center text-ink-500 text-sm">
          <i className="ri-loader-4-line animate-spin text-2xl text-brand-500 mr-2"></i>
          Loading checkout…
        </div>
      </SiteLayout>
    );
  }

  if (!product) {
    return (
      <SiteLayout navVariant="solid">
        <div className="min-h-[60vh] pt-32 flex flex-col items-center justify-center gap-4 px-4">
          <i className="ri-error-warning-line text-5xl text-ink-300"></i>
          <h1 className="font-display text-2xl font-bold text-ink-900">Service not found</h1>
          <Link
            to="/site/services"
            className="px-5 py-2.5 rounded-md text-sm font-semibold bg-ink-900 hover:bg-ink-800 text-white cursor-pointer"
          >
            Back to all services
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const image = product.media?.[0]?.url ?? "";

  return (
    <SiteLayout navVariant="solid">
      <div className="pt-24 md:pt-28 pb-16 md:pb-24 bg-cream-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs font-semibold text-ink-500 mb-6">
            <Link to="/site/services" className="hover:text-brand-600 cursor-pointer">Services</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/site/services/${product.id}`} className="hover:text-brand-600 cursor-pointer truncate max-w-[200px]">
              {product.name}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-ink-900">Checkout</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            {/* Form */}
            <form onSubmit={onSubmit} className="lg:col-span-8 space-y-6">
              <div>
                <h1 className="font-display text-3xl md:text-4xl font-bold text-ink-900 mb-2">
                  Book your move
                </h1>
                <p className="text-sm text-ink-500">
                  Lock in your date now — we'll confirm by phone within 1 business hour.
                </p>
              </div>

              {error && (
                <div className="flex items-start gap-2 px-4 py-3 rounded-md bg-rose-50 border border-rose-200 text-sm text-rose-700">
                  <i className="ri-error-warning-line text-base mt-0.5"></i>
                  <span>{error}</span>
                </div>
              )}

              {/* Customer */}
              <div className="bg-white rounded-2xl border border-ink-100 p-6 md:p-7">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-brand-100 text-brand-700 text-xs font-bold">1</div>
                  <h2 className="font-display text-lg font-bold text-ink-900">Your details</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="First name *">
                    <input className="form-input" value={form.first_name} onChange={update("first_name")} required />
                  </Field>
                  <Field label="Last name *">
                    <input className="form-input" value={form.last_name} onChange={update("last_name")} required />
                  </Field>
                  <Field label="Email *">
                    <input type="email" className="form-input" value={form.email} onChange={update("email")} required />
                  </Field>
                  <Field label="Phone *">
                    <input type="tel" className="form-input" value={form.phone} onChange={update("phone")} required placeholder="(347) 555-0123" />
                  </Field>
                </div>
              </div>

              {/* Pickup */}
              <div className="bg-white rounded-2xl border border-ink-100 p-6 md:p-7">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">2</div>
                  <h2 className="font-display text-lg font-bold text-ink-900">Pickup address</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
                  <Field label="Street address *" className="sm:col-span-6">
                    <input className="form-input" value={form.pickup_address} onChange={update("pickup_address")} required placeholder="412 Industry Court, Apt 3B" />
                  </Field>
                  <Field label="City *" className="sm:col-span-3">
                    <input className="form-input" value={form.pickup_city} onChange={update("pickup_city")} required />
                  </Field>
                  <Field label="State" className="sm:col-span-1">
                    <input className="form-input" value={form.pickup_state} onChange={update("pickup_state")} maxLength={2} placeholder="NY" />
                  </Field>
                  <Field label="ZIP" className="sm:col-span-2">
                    <input className="form-input" value={form.pickup_zip} onChange={update("pickup_zip")} />
                  </Field>
                </div>
              </div>

              {/* Dropoff */}
              <div className="bg-white rounded-2xl border border-ink-100 p-6 md:p-7">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-rose-100 text-rose-700 text-xs font-bold">3</div>
                  <h2 className="font-display text-lg font-bold text-ink-900">Drop-off address</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
                  <Field label="Street address *" className="sm:col-span-6">
                    <input className="form-input" value={form.dropoff_address} onChange={update("dropoff_address")} required />
                  </Field>
                  <Field label="City *" className="sm:col-span-3">
                    <input className="form-input" value={form.dropoff_city} onChange={update("dropoff_city")} required />
                  </Field>
                  <Field label="State" className="sm:col-span-1">
                    <input className="form-input" value={form.dropoff_state} onChange={update("dropoff_state")} maxLength={2} />
                  </Field>
                  <Field label="ZIP" className="sm:col-span-2">
                    <input className="form-input" value={form.dropoff_zip} onChange={update("dropoff_zip")} />
                  </Field>
                </div>
              </div>

              {/* Date */}
              <div className="bg-white rounded-2xl border border-ink-100 p-6 md:p-7">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-amber-100 text-amber-700 text-xs font-bold">4</div>
                  <h2 className="font-display text-lg font-bold text-ink-900">Preferred move date</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <Field label="Date *">
                    <input type="date" className="form-input" value={form.move_date} onChange={update("move_date")} required min={new Date().toISOString().slice(0, 10)} />
                  </Field>
                  <Field label="Start time">
                    <input type="time" className="form-input" value={form.move_time} onChange={update("move_time")} />
                  </Field>
                </div>
                <Field label="Notes for the crew (optional)">
                  <textarea
                    className="form-input min-h-[100px] resize-none"
                    value={form.notes}
                    onChange={update("notes")}
                    placeholder="Stairs, parking, fragile items, etc."
                    maxLength={500}
                  />
                </Field>
                <div className="text-[11px] text-ink-400 mt-1 text-right">
                  {form.notes.length}/500
                </div>
              </div>
            </form>

            {/* Summary */}
            <aside className="lg:col-span-4">
              <div className="bg-white rounded-2xl border border-ink-100 p-6 md:p-7 lg:sticky lg:top-28">
                <h3 className="font-display text-lg font-bold text-ink-900 mb-5">Order summary</h3>

                <div className="flex gap-4 pb-5 border-b border-ink-100">
                  {image && (
                    <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-cream-100">
                      <img src={image} alt={product.name} className="w-full h-full object-cover object-top" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-wider font-bold text-brand-600 mb-1">
                      {product.product_categories?.name ?? "Service"}
                    </div>
                    <div className="text-sm font-bold text-ink-900 leading-tight">
                      {product.name}
                    </div>
                    <div className="text-xs text-ink-500 mt-1">Qty 1 · Flat-rate</div>
                  </div>
                </div>

                <div className="space-y-2.5 py-5 border-b border-ink-100">
                  <Row label="Subtotal" value={`$${subtotal.toLocaleString()}`} />
                  <Row label={`Tax (${(taxRate * 100).toFixed(2)}%)`} value={`$${taxTotal.toLocaleString()}`} />
                  <Row label="Booking deposit" value="$0.00" muted />
                </div>

                <div className="flex items-center justify-between pt-5 mb-6">
                  <div className="text-sm font-bold text-ink-900">Total due</div>
                  <div className="font-display text-2xl font-bold text-ink-900">
                    ${total.toLocaleString()}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={onSubmit}
                  disabled={!requiredOk || submitting}
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-md text-sm font-bold bg-brand-500 hover:bg-brand-600 disabled:bg-ink-300 disabled:cursor-not-allowed text-white cursor-pointer whitespace-nowrap transition-colors"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Placing order…
                    </>
                  ) : (
                    <>
                      Place Booking
                      <i className="ri-arrow-right-line"></i>
                    </>
                  )}
                </button>

                <p className="text-[11px] text-ink-400 mt-4 leading-relaxed text-center">
                  No charge today. We'll confirm by phone, then collect a 20% deposit to lock in your date.
                </p>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}

function Field({ label, children, className = "" }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs font-semibold text-ink-600 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function Row({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className={muted ? "text-ink-400" : "text-ink-600"}>{label}</span>
      <span className={`font-semibold ${muted ? "text-ink-400" : "text-ink-900"}`}>{value}</span>
    </div>
  );
}